import { Box, Divider, Text } from '@wix/design-system';
import React, { useContext } from 'react';

import { RicosContext } from './../../../../../ricos-context';
import type {
  GalleryOptions_ItemStyle_Crop,
  GalleryOptions_Layout_Type,
  GalleryOptions_Thumbnails_Alignment,
} from './../../../../../ricos-schema/next-schema';
import { GalleryOptions_Layout_Orientation } from './../../../../../ricos-schema/next-schema';
import type { LogBiFn } from './../../../../../_shared/bi-context-provider/bi-context-provider';
import type { PmGalleryNode } from './../../../../../_shared/pm-node-types';
import { SliderWithInput, usePanelContainerElement } from './../../../../../ui-components';

import {
  LAYOUT_SETTINGS_ITEMS_PER_ROW_INPUT,
  LAYOUT_SETTINGS_ITEMS_PER_ROW_SLIDER,
  LAYOUT_SETTINGS_SPACING_BETWEEN_ITEMS_INPUT,
  LAYOUT_SETTINGS_SPACING_BETWEEN_ITEMS_SLIDER,
  LAYOUT_SETTINGS_SPACING_BETWEEN_THUMBNAILS_INPUT,
  LAYOUT_SETTINGS_SPACING_BETWEEN_THUMBNAILS_SLIDER,
  LAYOUT_SETTINGS_THUMBNAIL_SIZE_INPUT,
  LAYOUT_SETTINGS_THUMBNAIL_SIZE_SLIDER,
} from '../../../../data-hooks';
import { ImageOrientationControl } from './image-orientation-control';
import { ImageRatioControl } from './image-ratio-control';
import { ScrollDirectionControl } from './scroll-direction-control';
import { ThumbnailAlignmentControl } from './thumbnail-alignment-control';
import { ThumbnailCropControl } from './thumbnail-crop-control';

type LayoutControlsSectionProps = {
  layout: GalleryOptions_Layout_Type;
  data: PmGalleryNode['attrs'];
  updateData: (data: PmGalleryNode['attrs']) => void;
  logBi: LogBiFn;
};

type SettingControl =
  | 'scrollDirection'
  | 'imageOrientation'
  | 'thumbnailSize'
  | 'spacing'
  | 'itemsPerRow'
  | 'thumbnailCrop'
  | 'imageRatio'
  | 'thumbnailAlignment'
  | 'thumbnailSpacing'
  | 'divider';

const controlsByLayout: Record<GalleryOptions_Layout_Type, SettingControl[]> = {
  COLLAGE: ['scrollDirection', 'imageOrientation', 'divider', 'thumbnailSize', 'spacing'],
  MASONRY: ['imageOrientation', 'divider', 'thumbnailSize', 'spacing'],
  GRID: ['thumbnailCrop', 'imageRatio', 'divider', 'itemsPerRow'],
  THUMBNAIL: ['thumbnailAlignment', 'divider', 'thumbnailSpacing'],
  SLIDER: ['spacing', 'thumbnailCrop', 'imageRatio'],
  SLIDESHOW: [],
  PANORAMA: ['spacing'],
  COLUMN: ['spacing'],
  MAGIC: [],
  FULLSIZE: [],
};

export const LayoutControlsSection: React.FC<LayoutControlsSectionProps> = ({
  data,
  updateData,
  layout,
  logBi,
}) => {
  const { t } = useContext(RicosContext);
  const containerEl = usePanelContainerElement();

  const getValueFromOptionsItem = (name: 'targetSize' | 'spacing' | 'crop' | 'ratio') =>
    data.options?.item?.[name];

  const getValueFromOptionsThumbnails = (name: 'spacing' | 'placement') =>
    data.options?.thumbnails?.[name];

  const getNumericValue = (value: $TSFixMe, defaultValue: $TSFixMe) =>
    typeof value === 'number' ? value : defaultValue;

  const applyGallerySetting = (setting: $TSFixMe, key: $TSFixMe) => {
    const componentData = {
      ...data,
      options: {
        ...data.options,
        [key]: { ...data.options?.[key as $TSFixMeKey<typeof data.options>], ...setting },
      },
    };
    updateData(componentData);
  };

  const controls: Record<SettingControl, React.ReactNode> = {
    itemsPerRow: (
      <SliderWithInput
        min={1}
        max={5}
        label={t('GallerySettings_Sliders_Items_Per_Row')}
        sliderDataHook={LAYOUT_SETTINGS_ITEMS_PER_ROW_SLIDER}
        inputDataHook={LAYOUT_SETTINGS_ITEMS_PER_ROW_INPUT}
        onChange={value => applyGallerySetting({ numberOfColumns: value }, 'layout')}
        tooltipAppendTo={containerEl}
        value={data.options?.layout?.numberOfColumns ?? 1}
        logBi={logBi}
      />
    ),
    thumbnailSize: (
      <SliderWithInput
        min={10}
        max={1000}
        sliderDataHook={LAYOUT_SETTINGS_THUMBNAIL_SIZE_SLIDER}
        inputDataHook={LAYOUT_SETTINGS_THUMBNAIL_SIZE_INPUT}
        onChange={value => applyGallerySetting({ targetSize: value }, 'item')}
        value={getNumericValue(getValueFromOptionsItem('targetSize'), 300)}
        label={
          data.options?.layout?.orientation === 'COLUMNS'
            ? t('GallerySettings_LayoutControlSection_Column')
            : t('GallerySettings_LayoutControlSection_Row')
        }
        tooltipAppendTo={containerEl}
        logBi={logBi}
      />
    ),
    spacing: (
      <SliderWithInput
        onChange={value => applyGallerySetting({ spacing: value }, 'item')}
        value={getNumericValue(getValueFromOptionsItem('spacing'), 5)}
        label={t('GallerySettings_Spacing_Between_Items')}
        sliderDataHook={LAYOUT_SETTINGS_SPACING_BETWEEN_ITEMS_SLIDER}
        inputDataHook={LAYOUT_SETTINGS_SPACING_BETWEEN_ITEMS_INPUT}
        tooltipTextKey={t('GallerySettings_Spacing_Between_Items_Tooltip')}
        min={0}
        max={100}
        tooltipAppendTo={containerEl}
        logBi={logBi}
      />
    ),
    thumbnailSpacing: (
      <SliderWithInput
        onChange={value => applyGallerySetting({ spacing: Math.trunc(value) }, 'thumbnails')}
        value={getNumericValue(getValueFromOptionsThumbnails('spacing'), 5)}
        label={t('GallerySettings_Spacing_Between_Items')}
        sliderDataHook={LAYOUT_SETTINGS_SPACING_BETWEEN_THUMBNAILS_SLIDER}
        inputDataHook={LAYOUT_SETTINGS_SPACING_BETWEEN_THUMBNAILS_INPUT}
        tooltipTextKey={t('GallerySettings_Spacing_Between_Items_Tooltip')}
        min={0}
        max={100}
        tooltipAppendTo={containerEl}
        logBi={logBi}
      />
    ),
    thumbnailCrop: (
      <ThumbnailCropControl
        onChange={value => applyGallerySetting({ crop: value }, 'item')}
        value={getValueFromOptionsItem('crop') as GalleryOptions_ItemStyle_Crop}
        logBi={logBi}
      />
    ),
    imageRatio: (
      <ImageRatioControl
        onChange={value => applyGallerySetting({ ratio: value }, 'item')}
        value={getValueFromOptionsItem('ratio') as number}
        logBi={logBi}
      />
    ),
    imageOrientation: (
      <ImageOrientationControl
        onChange={value => applyGallerySetting({ orientation: value }, 'layout')}
        value={data.options?.layout?.orientation ?? GalleryOptions_Layout_Orientation.COLUMNS}
        logBi={logBi}
      />
    ),
    scrollDirection: (
      <ScrollDirectionControl
        onChange={value =>
          applyGallerySetting({ horizontalScroll: value === 'horizontal' }, 'layout')
        }
        value={data.options?.layout?.horizontalScroll ? 'horizontal' : 'vertical'}
        logBi={logBi}
      />
    ),
    thumbnailAlignment: (
      <ThumbnailAlignmentControl
        onChange={value => applyGallerySetting({ placement: value }, 'thumbnails')}
        value={getValueFromOptionsThumbnails('placement') as GalleryOptions_Thumbnails_Alignment}
        logBi={logBi}
      />
    ),
    divider: <Divider />,
  };

  if (controlsByLayout[layout].length === 0) {
    return null;
  }

  return (
    <>
      <Box marginTop="SP3" marginBottom="SP2" marginInline="-24px">
        <Divider />
      </Box>
      <Box direction="vertical" gap="SP3">
        <Text weight="bold" size="small">
          {t('GalleryPlugin_Layout_Options_Title')}
        </Text>
        {controlsByLayout[layout].map(name => controls[name])}
      </Box>
    </>
  );
};
